import { useState } from 'react';
import { ParkingaboProductListItem } from '../../../shared/ParkingaboProductModels';
import { ProductDetailBody, ProductOverviewList } from './ProductsDetailOutlet';
import { useLoggedParkingaboServerWrite } from '../../../api/ParkingaboApi';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage';
import { FeedbackPopup } from '../../../components/FeedbackPopup';
import { Localized } from 'dg-web-shared/lib/Localization';
import { Typography } from '@mui/material';
import { CancelSuccessDialog } from './ProductsDetailOutletReservation';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';
import {
    MenuListItemColorVariant,
    ParkingaboListItem,
    ParkingaboListItemLabel,
} from '../../../components/ParkingaboMenuListItem.tsx';
import { Delete } from '@mui/icons-material';
import {
    contractReceiptUrl,
    ReceiptButton,
} from '../account-statement/AccountStatementOutlet.tsx';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';
import { ParkingaboProductType } from 'product-shared/product-template/ParkingaboProductType.ts';

interface GuestReservationCancellationPayload {
    contractExternalId: string;
}

export function ProductDetailGuestReservation({
    product,
    refetchProducts,
}: {
    product: ParkingaboProductListItem;
    refetchProducts: () => void;
}) {
    const navigate = useNavigate();
    const { user } = useCustomerContext();
    const [cancelConfirmDialogOpen, setCancelConfirmDialogOpen] =
        useState(false);
    const [cancelSuccessDialogOpen, setCancelSuccessDialogOpen] =
        useState(false);
    const [cancellationState, cancelGuestReservation] =
        useLoggedParkingaboServerWrite<
            GuestReservationCancellationPayload,
            ValidationData
        >('storno-product', ({ contractExternalId }) => ({
            url: `user/self/product/${contractExternalId}`,
            method: RequestMethod.DELETE,
        }));
    useServerSuccessEffect(cancellationState, () => {
        setCancelSuccessDialogOpen(true);
    });
    const validFrom = DateTime.fromISO(product.validFrom);
    const showCancelButton =
        DateTime.now() < validFrom.minus({ minute: 1 }) &&
        product.type === ParkingaboProductType.GUEST_RESERVATION_UBS;
    return (
        <ProductDetailBody>
            <BackendRequestErrorMessage requestState={cancellationState} />
            <ProductOverviewList product={product} />
            {showCancelButton && (
                <ParkingaboListItem
                    onClick={() => setCancelConfirmDialogOpen(true)}
                    colorVariant={MenuListItemColorVariant.PRIMARY}
                >
                    <ParkingaboListItemLabel
                        loading={
                            cancellationState.status === RequestStatus.PENDING
                        }
                        label={
                            <Localized
                                de="Reservierung löschen"
                                fr="Supprimer la réservation"
                                it="Cancella riservazione"
                                en="Delete reservation"
                            />
                        }
                        icon={
                            <Delete
                                style={{ fontSize: 36 }}
                                color={'primary'}
                            />
                        }
                    />
                </ParkingaboListItem>
            )}
            {product.hasBooking && (
                <ReceiptButton
                    url={contractReceiptUrl(
                        user.operatorExternalId,
                        product.contractExternalId,
                        product.token,
                    )}
                />
            )}

            <CancellationConfirmDialog
                open={cancelConfirmDialogOpen}
                onAbort={() => setCancelConfirmDialogOpen(false)}
                onConfirm={() => {
                    cancelGuestReservation({
                        contractExternalId: product.contractExternalId,
                    });
                    setCancelConfirmDialogOpen(false);
                }}
            />
            <CancelSuccessDialog
                open={cancelSuccessDialogOpen}
                onClick={() => {
                    refetchProducts();
                    setCancelSuccessDialogOpen(false);
                    navigate('..');
                }}
            />
        </ProductDetailBody>
    );
}

function CancellationConfirmDialog({
    open,
    onAbort,
    onConfirm,
}: {
    open: boolean;
    onAbort: () => void;
    onConfirm: () => void;
}) {
    return (
        <FeedbackPopup
            open={open}
            color="warning"
            title={
                <Localized
                    de="Reservierung löschen"
                    fr="Supprimer la réservation"
                    it="Cancella riservazione"
                    en="Delete reservation"
                />
            }
            onAbort={onAbort}
            onConfirm={onConfirm}
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
        >
            <Typography component="p" sx={{ paddingBottom: 1 }}>
                <Localized
                    de="Möchten Sie diese Reservierung wirklich löschen?"
                    fr="Voulez-vous vraiment supprimer cette réservation?"
                    it="Vuole veramente cancellare questa riservazione?"
                    en="Do you really want to delete this reservation?"
                />
            </Typography>
            <Typography>
                <Localized
                    de="Bitte informieren Sie den Gast, dass der QR-Code für die Einfahrt zum Parking nicht mehr gültig ist."
                    fr="Veuillez informer le visiteur que le code QR pour entrer dans le parking n'est plus valide."
                    it="La preghiamo di informare l'ospite che il codice QR per l'ingresso nel parcheggio non è più valido."
                    en="Please inform the guest that the QR code for entering the parking is no longer valid."
                />
            </Typography>
        </FeedbackPopup>
    );
}
